import { useEffect, useRef } from 'react';
import { EnvironmentOutlined } from '@ant-design/icons';
import { Select } from 'uiKit/atoms/Select/Select';
import { FormControl } from 'uiKit/atoms/FormControl/FormControl';
import { MenuItem } from 'uiKit/atoms/MenuItem/MenuItem';
import { Typography } from 'uiKit/atoms/Typography/Typography';
import { Input } from 'uiKit/atoms/Input/Input';
import { PrimaryButton } from 'uiKit/atoms/Button/components/PrimaryButton';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { observer } from 'mobx-react';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { renderError } from '../../helpers/utils';
import { phoneRegExp, RequiredFieldString } from '../../constants';
import { useStores } from 'stores';
import styles from './Profile.module.scss';

const ProfilePageSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .max(15, "The length of phone number must be 15 characters or fewer. Can have numbers and symbol '+'")
    .matches(phoneRegExp, 'Please enter valid phone number.')
    .required(RequiredFieldString),
  locationId: Yup.number().required(RequiredFieldString),
  preferredCurrency: Yup.string().required(RequiredFieldString),
});

const ProfilePage = () => {
  const { userStore } = useStores();
  const { locations, currencies } = userStore;
  const navigate = useNavigate();

  const formRef = useRef();

  const submitHandler = () => formRef.current.handleSubmit();

  useEffect(() => {
    userStore.getLocations();
    userStore.getCurrencies();
    userStore.getUserProfile();
  }, []);

  const initialValues = {
    firstName: userStore.userProfile.firstName,
    lastName: userStore.userProfile.lastName,
    email: userStore.userProfile.email,
    phoneNumber: userStore.userProfile.phoneNumber,
    locationId:
      userStore.userProfile.locationId === '' || userStore.userProfile.locationId === null
        ? 0
        : Number(userStore.userProfile.locationId),
    preferredCurrency: userStore.userProfile.preferredCurrency,
  };

  return (
    <main>
      <section className={styles.bns3_myprofile_block}>
        <div className={styles.bns3_mainContainer}>
          <div className={styles.bns3_myprofile_control_panel_container}>
            <h1 className={styles.bns3_myprofile_title}>My Profile</h1>

            <div className={styles.bns3_myprofile_controls_container}>
              <div className={styles.bns3_myprofile_button_container}>
                <PrimaryButton
                  variant="text"
                  shape="round"
                  size="large"
                  className={styles.bns3_myprofile_save_button}
                  onClick={submitHandler}
                >
                  Save
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="classic container-fluid">
        <div className={styles.bns3_profile_form_container}>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={ProfilePageSchema}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={values => {
              userStore.updateUserProfile({ ...values, locationId: parseInt(values.locationId) }, navigate);
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
              <div className={styles.bns3_mainContainer}>
                <form onSubmit={handleSubmit} className={styles.bns3_form_container}>
                  <h2 className={styles.bns3_block_title}>Personal</h2>
                  <div className={styles.bns3_inputs_container}>
                    <div className={styles.bns3_input_wrapper}>
                      <label htmlFor="InputFirstName" className={styles.bns3_label}>
                        First Name
                      </label>
                      <Input
                        disabled
                        disableUnderline
                        type="text"
                        name="firstName"
                        onChange={handleChange}
                        value={values.firstName}
                        className={cn(styles.bns3_input, {
                          'is-invalid': errors.firstName && touched.firstName,
                        })}
                        id="InputFirstName"
                      />
                    </div>
                    <div className={styles.bns3_input_wrapper}>
                      <label htmlFor="InputLastName" className={styles.bns3_label}>
                        Last Name
                      </label>
                      <Input
                        disabled
                        disableUnderline
                        type="text"
                        name="lastName"
                        value={values.lastName}
                        className={cn(styles.bns3_input, {
                          'is-invalid': errors.lastName && touched.lastName,
                        })}
                        id="InputLastName"
                      />
                    </div>
                    <div className={styles.bns3_input_wrapper}>
                      <label htmlFor="InputPhoneNumber" className={styles.bns3_label}>
                        Phone Number
                        <span>*</span>
                      </label>
                      <Input
                        autoComplete="new-password"
                        name="phoneNumber"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phoneNumber}
                        className={cn(styles.bns3_input, {
                          'is-invalid': errors.phoneNumber && touched.phoneNumber,
                        })}
                        id="InputPhoneNumber"
                        aria-describedby="PhoneNumber"
                        placeholder="Phone number"
                        data-toggle="tooltip"
                        data-placement="top"
                        disableUnderline
                      />
                      {renderError(errors, 'phoneNumber', touched)}
                    </div>
                    <div className={styles.bns3_flex_container}>
                      <div className={styles.bns3_input_wrapper}>
                        <FormControl fullWidth size="small">
                          <Typography
                            sx={{
                              display: 'block',
                              mb: '10px',
                              fontSize: '20px',
                              lineHeight: 2,
                              color: '#000',
                              fontWeight: 500,
                            }}
                          >
                            Location
                          </Typography>
                          <Select
                            labelId="locationId-label"
                            id="locationId"
                            name="locationId"
                            value={values.locationId}
                            onChange={e => setFieldValue('locationId', e.target.value)}
                            onBlur={handleBlur}
                            sx={{
                              width: '100%',
                              minWidth: '200px',
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            }}
                          >
                            {locations.map(location => (
                              <MenuItem key={location.id} value={location.id}>
                                <div className={styles.bns3_select_location_label}>
                                  <EnvironmentOutlined className={styles.bns3_select_location_icon} />
                                  {location.name}
                                </div>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {renderError(errors, 'locationId', touched)}
                      </div>

                      <div className={styles.bns3_input_wrapper}>
                        <FormControl fullWidth size="small">
                          <Typography
                            sx={{
                              display: 'block',
                              mb: '10px',
                              fontSize: '20px',
                              lineHeight: 2,
                              color: '#000',
                              fontWeight: 500,
                            }}
                          >
                            Currency
                          </Typography>
                          <Select
                            labelId="preferredCurrency-label"
                            id="preferredCurrency"
                            name="preferredCurrency"
                            value={values.preferredCurrency || ''}
                            onChange={e => setFieldValue('preferredCurrency', e.target.value)}
                            onBlur={handleBlur}
                            disabled={!currencies.length}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            }}
                          >
                            {currencies.map(currency => (
                              <MenuItem key={currency} value={currency}>
                                {currency}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {renderError(errors, 'preferredCurrency', touched)}
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </Formik>
        </div>
      </section>
    </main>
  );
};

ProfilePage.propTypes = {
  userStore: PropTypes.shape({
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    userProfile: PropTypes.object,
  }),
};

export default observer(ProfilePage);
